import React, { useEffect, useState } from 'react';
import { TagSelect } from '@Shared.Angular/flowingly.components/TagSelect/TagSelect';
import TagList from '@Shared.Angular/flowingly.components/TagList/TagList';
import ITagGroupDetail from '@Shared.Angular/@types/core/contracts/queryModel/tagGroup/tagGroupDetail';
import { Guid } from '@Shared.Angular/@types/guid';
import { getService } from '@Shared.Angular/flowingly.hooks/useService';
import { TextButton } from '../buttons/TextButton';

export type Props = {
  taggedEntityType: string;
  selectedTagIds: Guid[];
  editable: boolean;
  onSaved: (tags: Guid[]) => void;
};

export const TagEditor = ({
  taggedEntityType,
  selectedTagIds,
  editable,
  onSaved
}: Props) => {
  const tagGroupApiService = getService('tagGroupApiService');
  const pubsubService = getService('pubsubService');
  const subscriberId = 'TagEditorComponent';

  const [editMode, setEditMode] = useState(false);
  const [tagGroups, setTagGroups] = useState<ITagGroupDetail[]>([]);
  const [initialSelectedTagIds, setInitialSelectedTagIds] = useState<Guid[]>(
    []
  );
  const [pendingSelectedTagIds, setPendingSelectedTagIds] = useState<Guid[]>(
    []
  );

  useEffect(() => {
    getTagGroups();
    pubsubService.subscribeLate(
      'TAG_GROUP_DELETED',
      getTagGroups,
      subscriberId
    );
    pubsubService.subscribeLate(
      'TAG_GROUP_CREATED',
      getTagGroups,
      subscriberId
    );
    pubsubService.subscribeLate(
      'TAG_GROUP_UPDATED',
      getTagGroups,
      subscriberId
    );
    return () => {
      pubsubService.unsubscribeAll(subscriberId);
    };
  }, []);

  const getTagGroups = () => {
    tagGroupApiService.getAllTagGroups().then((newTagGroups) => {
      setTagGroups(newTagGroups);
    });
  };

  useEffect(() => {
    setInitialSelectedTagIds(selectedTagIds || []);
  }, [selectedTagIds]);

  const handleCancel = () => {
    setPendingSelectedTagIds(initialSelectedTagIds);
    setEditMode(false);
  };

  const handleSave = () => {
    setEditMode(false);
    setInitialSelectedTagIds(pendingSelectedTagIds);
    onSaved?.(pendingSelectedTagIds);
  };

  const initialSelectedTagsByGroupName = tagGroups.reduce<
    Record<string, { tagNames: string[]; color: string }>
  >((acc, group) => {
    const matched = group.tags.filter((tag) =>
      initialSelectedTagIds.includes(tag.id)
    );
    if (matched.length > 0) {
      acc[group.name] = {
        tagNames: matched.map((t) => t.name),
        color: group.color
      };
    }
    return acc;
  }, {});

  return (
    <div className="tag-editor-container">
      <label className="title">{taggedEntityType} Tags</label>
      {!editMode && (
        <div>
          <TagList tagsByGroupName={initialSelectedTagsByGroupName} />
          <div className="add-remove-button-container">
            {editable && (
              <TextButton
                type="primary-text-only"
                text="Add or Remove"
                classNames="add-remove-button"
                onClick={() => {
                  if (editable) {
                    setPendingSelectedTagIds([...initialSelectedTagIds]);
                    setEditMode(true);
                  }
                }}
              />
            )}
          </div>
        </div>
      )}
      {editMode && (
        <div className="tag-editor-select-container">
          <TagSelect
            allTagGroups={tagGroups}
            selectedTagIds={pendingSelectedTagIds}
            onChange={(updated: Guid[]) => setPendingSelectedTagIds(updated)}
          />
          <div className="tag-actions-container">
            <TextButton type="text-only" onClick={handleCancel} text="Cancel" />
            <TextButton
              testId="tag-editor-done-button"
              type="primary"
              onClick={handleSave}
              text="Done"
            />
          </div>
        </div>
      )}
    </div>
  );
};
